import {SpriteGraphics} from '@fantasmagames/core/src/display/SpriteGraphics';
import {Scrollbar, I2dLimits} from '@fantasmagames/slot/src/modules/popupmenus/Scrollbar';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {ResolutionManager} from '@fantasmagames/core/src/screen/ResolutionManager';
import {Device} from '@fantasmagames/core/src/device/Device';
import {EventManagerWithAsync} from '@fantasmagames/core/src/events/EventManagerWithAsync';

export class HTMLPageComponent {
	protected _background: PIXI.Texture;
	protected _gameCanvas: HTMLElement;

	protected _uiStageOffsetY: number = 0;
	protected _uiStageMaxY: number = 0;

	private _mainContainer: HTMLElement;
	private _mainBody: HTMLElement;

	protected _hasLoaded: boolean = false;

	////////////////////////////////////////////////////////////////////
	// IOS touch and input.
	////////////////////////////////////////////////////////////////////
	protected onDragStopBoundFunction: (this: Document, ev: TouchEvent) => any;
	protected _dragInput: PIXI.Sprite;
	protected _yOffset: number = 0;
	protected _dragStartY: number;
	protected _minYDrag: number = 0;
	protected _maxYDrag: number = 0;
	protected _isDragging: boolean;

	protected _resourcesPath: string = 'HTML/';

	protected _containerId: string = 'FANTASMA_CONTAINER';
	protected _scrollBar: Scrollbar;

	protected resolutionManager: ResolutionManager;
	protected resources: ResourcesLoader;
	protected device: Device;
	protected eventManager: EventManagerWithAsync;

	protected _container: PIXI.Container;

	protected _htmlContent: string = '<div></div>';

	constructor(resourceloader: ResourcesLoader, device: Device, resolutionManager: ResolutionManager) {
		this.resources = resourceloader;
		this.device = device;
		this.resolutionManager = resolutionManager;

		this._container = new PIXI.Container();
	}

	public destroy(): void {
		this.removeAllInput();

		this.removeElement(this._mainContainer);
		this.removeElement(this._mainBody);

		this.container.destroy();
	}

	protected removeElement(element: HTMLElement): void {
		const parent = element.parentElement;
		if (parent) {
			parent.removeChild(element);
		}
	}

	public appendChild(item: HTMLElement): void {
		this._mainBody.appendChild(item);
	}

	public init(limits: I2dLimits, resourcePath: string, containerId: string): void {
		this.setupPathsAndIds(resourcePath, containerId);

		this._gameCanvas = document.getElementById('GameCanvas');

		this.setupUiOffset(limits);
		this.setupHTML();

		this.hide();
	}

	public show(): void {
		this._mainContainer.style.display = this.device.isIE || this.device.isMacOs ? 'block' : 'grid';
		this._mainContainer.style.visibility = 'visible';

		if (this.device.isMobile || this.device.hasTouch) {
			this._minYDrag = this._mainBody.offsetHeight - this.resolutionManager.halfResolution.height * 0.5;
			this._minYDrag *= -1;

			if (!this._scrollBar) {
				this.addScrollbar();
			}
		}
	}

	public hide(): void {
		this._mainContainer.style.display = 'none';
		this._mainContainer.style.visibility = 'hidden';
	}

	protected setupPathsAndIds(resourcePath: string, containerId: string): void {
		this._resourcesPath = resourcePath;
		this._containerId = containerId;
	}

	protected setupUiOffset(limits: I2dLimits): void {
		this._uiStageOffsetY = limits.minY;
		this._uiStageMaxY = limits.maxY;
	}

	protected setupHTML(): void {
		this.setupMainContainer();

		this.setupMainDiv();
		this._mainContainer.appendChild(this._mainBody);

		this._gameCanvas.parentElement.appendChild(this._mainContainer);

		this.resizeHTML();

		this._mainContainer.style.transformOrigin = `0px 0px`;
		const totalHeight: number = this._uiStageMaxY - this._uiStageOffsetY;
		this._mainContainer.style.maxHeight = this._mainContainer.style.height = `${totalHeight}px`;
	}

	protected setupMainContainer(): void {
		this._mainContainer = document.createElement('div');
		this._mainContainer.id = this._containerId;
		this._mainContainer.style.display = 'none';
		this._mainContainer.style.position = 'absolute';
		this._mainContainer.style.visibility = 'hidden';

		this._mainContainer.style.height = this.resolutionManager.resolution.height - this._uiStageOffsetY + 'px';
		this._mainContainer.style.width = this.resolutionManager.resolution.width + 'px';
	}

	protected setupMainDiv(): void {
		this._mainBody = document.createElement('div');
		this._mainBody.innerHTML = this.getBodyContent();
		this._mainBody.classList.add('FANTASMA_HTML_COMPONENT');

		// this._mainBody.style.width = '100%';
		this._mainBody.style.width = this.device.isMobile ? this._mainContainer.style.width : '100%';
		this._mainBody.style.height = '100%';
	}

	protected getBodyContent(): string {
		return this._htmlContent;
	}

	public setBodyContent(content: string): void {
		this._htmlContent = content;
		this._mainBody.innerHTML = content;

		this.onLoadComplete();
	}

	/**
	 * Only use for Iframe which is unpreffered.
	 */
	protected onLoadComplete(): void {
		this._hasLoaded = true;

		if (this.device.isMobile) {
			this.setupForMobile();
		} else {
			this.setupInputForDesktop();
		}
	}

	protected setupInputForDesktop(): void {
		this._mainContainer.style.overflowY = 'scroll';
		this._mainContainer.style.webkitUserSelect = 'none';
		this._mainContainer.style.userSelect = 'none';
		this._mainContainer.style.msUserSelect = 'none';
		this._mainContainer.style.touchAction = 'none';

		this._mainBody.style.pointerEvents = 'none';
		this._mainBody.style.touchAction = 'none';
	}

	protected setupForMobile(): void {
		this._mainContainer.style.overflowY = 'scroll';
		this._mainContainer.style.pointerEvents = 'none';
		this._mainContainer.style.touchAction = 'none';
		this._mainBody.style.pointerEvents = 'none';
		this._mainBody.style.touchAction = 'none';

		this.setupInput();
	}

	protected addScrollbar(): void {
		const contentHeight: number = this._uiStageMaxY - this._uiStageOffsetY;
		const scrollBarWidth: number = 12;

		this._scrollBar = new Scrollbar(scrollBarWidth, contentHeight, contentHeight, this._mainBody.scrollHeight);
		this._container.addChild(this._scrollBar);
		this._scrollBar.position.set(this.resolutionManager.resolution.width - scrollBarWidth, this._uiStageOffsetY);
	}

	protected setupInput(): void {
		this._dragInput = SpriteGraphics.makeHiddenRectangle(
			this.resolutionManager.resolution.width,
			this.resolutionManager.resolution.height,
		);

		if (!__PRODUCTION__) {
			console.warn(
				this.resolutionManager.resolution.width,
				this._uiStageMaxY - this._uiStageOffsetY,
				this._uiStageOffsetY,
				this._uiStageMaxY,
			);
			console.warn(this._dragInput);
		}

		this._dragInput.interactive = true;
		this._container.addChild(this._dragInput);
		this._dragInput.position.y = this._uiStageOffsetY;
		this._dragInput.on('touchstart', this.onInputTouchStart.bind(this));
		this._dragInput.on('mousedown', this.onInputTouchStart.bind(this));

		this.onDragStopBoundFunction = this.removeDragInput.bind(this);
	}

	protected removeAllInput(): void {
		if (this._dragInput) {
			this._dragInput.interactive = false;
			this._dragInput = null;

			this.removeDragInput();
		}
	}

	protected onInputTouchStart(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (this._isDragging) {
			return;
		}

		this._isDragging = true;

		document.addEventListener('touchend', this.onDragStopBoundFunction);
		document.addEventListener('touchcancel', this.onDragStopBoundFunction);
		document.addEventListener('mouseup', this.onDragStopBoundFunction);
		document.addEventListener('mouseleave', this.onDragStopBoundFunction);
		document.addEventListener('mouseout', this.onDragStopBoundFunction);

		this._dragStartY = event.data.global.y;

		this._dragInput.on('touchmove', this.onInputMove.bind(this));
		this._dragInput.on('mousemove', this.onInputMove.bind(this));
	}

	protected removeDragInput(): void {
		document.removeEventListener('touchend', this.onDragStopBoundFunction);
		document.removeEventListener('touchcancel', this.onDragStopBoundFunction);

		document.removeEventListener('mouseup', this.onDragStopBoundFunction);
		document.removeEventListener('mouseleave', this.onDragStopBoundFunction);
		document.removeEventListener('mouseout', this.onDragStopBoundFunction);

		if (this._dragInput) {
			this._dragInput.removeListener('touchmove');
			this._dragInput.removeListener('mousemove');
		}

		this._isDragging = false;
	}

	protected onInputMove(event: PIXI.interaction.InteractionEvent): void {
		if (this.device.isMobile) {
			this._minYDrag = this._mainBody.offsetHeight - this.resolutionManager.halfResolution.height * 0.5;
			this._minYDrag *= -1;

			if (!this._scrollBar) {
				this.addScrollbar();
			}
		}

		const deltaY: number = event.data.global.y - this._dragStartY;

		this._dragStartY = event.data.global.y;

		this._yOffset += deltaY;
		this._yOffset = Math.max(Math.min(this._yOffset, this._maxYDrag), this._minYDrag);
		this._mainBody.style.webkitTransform = `translateY(${this._yOffset}px)`;
		this._mainBody.style.transform = `translateY(${this._yOffset}px)`;

		const positionScalar: number = GameUtils.normalize(this._yOffset, 0, this._minYDrag);

		this._scrollBar.moveBarObject(1 - positionScalar);
	}

	public replaceImages(): void {
		const images = this._mainBody.getElementsByTagName('img');

		let originalPath = '';

		for (let i = 0; i < images.length; i++) {
			const image: HTMLImageElement = images[i];
			originalPath = image.getAttribute('src').replace('./', '');

			const imageResource: string = this.resources.getDevicePathForResource(this._resourcesPath + originalPath);
			image.setAttribute('src', imageResource);
		}
	}

	public resizeHTML(): void {
		const scale = parseFloat(this._gameCanvas.style.width) / this.resolutionManager.resolution.width;
		this._mainContainer.style.webkitTransform = `scale(${scale})`;
		this._mainContainer.style.transform = `scale(${scale})`;
		this._mainContainer.style.top = `${this._uiStageOffsetY * scale}px`;
		this._mainContainer.style.marginLeft = `${this._gameCanvas.style.marginLeft}`;
		this._mainContainer.style.marginTop = `${this._gameCanvas.style.marginTop}`;
	}

	protected goToPage(id: string): void {
		// TODO: If time,
		// Maybe implement as a regular HTML goto.
	}

	public get container(): PIXI.Container {
		return this._container;
	}

	public get mainContainer(): HTMLElement {
		return this._mainContainer;
	}

	public get mainBody(): HTMLElement {
		return this._mainBody;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/html/HTMLPageComponent.ts